import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  closeDesktopCallWindow,
  isDesktopCallWindow,
  navigateDesktopWorkspace,
} from '@/features/desktop/callWindow';

export interface CallSurfaceExit {
  /**
   * The call is over and there is nowhere in particular to be. In a browser,
   * go to `fallbackPath`; in a call window, close the window.
   */
  leave(fallbackPath: string): void;
  /**
   * The call turned into something that is NOT a call and the user has to end
   * up there. In a browser, navigate. In a call window, send the WORKSPACE
   * there and close this window behind you.
   */
  moveTo(path: string): void;
}

/**
 * LEAVING A CALL SURFACE — the rule that four places kept getting wrong.
 *
 * A desktop call window is a call and nothing else. Navigating it anywhere else
 * turns it into a SECOND WORKSPACE, with its own sidebar, its own chat list and
 * its own everything — which is precisely the duplicate the whole arrangement
 * exists to avoid. `CallSurface.leaveCall` was guarded against that. Three more
 * exits were not, and every one of them is reachable from the More menu inside
 * the call window:
 *
 *   blind transfer      → `/calls`, the call HISTORY page, in a call window;
 *   complete transfer   → the same;
 *   escalate to meeting → `/meet/:id`, so the call window became a full meeting
 *                         room while the shell still had it keyed to a call
 *                         that no longer existed. Its close button hung up
 *                         nothing, and it never closed itself.
 *
 * The two transfers `leave`: you handed the call over, you are not on it any
 * more, and there is no reason to drag the workspace to a list of finished
 * calls — closing the window IS the return to what the user was doing. The
 * escalation `moveTo`s: a meeting is a place you must actually arrive at, so
 * the workspace goes there and the call window, whose call is over, gets out of
 * the way. That is also what Teams does with both.
 */
export function useCallSurfaceExit(): CallSurfaceExit {
  const navigate = useNavigate();

  const leave = useCallback(
    (fallbackPath: string) => {
      if (isDesktopCallWindow()) {
        closeDesktopCallWindow('');
        return;
      }
      navigate(fallbackPath);
    },
    [navigate],
  );

  const moveTo = useCallback(
    (path: string) => {
      if (isDesktopCallWindow()) {
        navigateDesktopWorkspace(path);
        closeDesktopCallWindow('');
        return;
      }
      navigate(path);
    },
    [navigate],
  );

  return useMemo(() => ({ leave, moveTo }), [leave, moveTo]);
}
